import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';
import { MdOutlineArrowForwardIos } from 'react-icons/md';
import { SlArrowRight } from 'react-icons/sl';
import { FaExpand } from 'react-icons/fa';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showNav: false,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const thumbnailContainerRef = useRef(null);
  
  const { intl, rootClassName, className, images, imageVariants, thumbnailVariants } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  
  // Check if we need thumbnail navigation (more than 3 images)
  const needsThumbnailNav = images.length > 3;
  
  // Function to check scroll position and update arrow visibility
  const updateArrowVisibility = () => {
    if (!thumbnailContainerRef.current || !needsThumbnailNav) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }
    
    const container = thumbnailContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };
  
  // Scroll functions for thumbnail navigation
  const scrollThumbnails = useCallback((direction) => {
    if (!thumbnailContainerRef.current) return;
    
    const container = thumbnailContainerRef.current;
    const scrollAmount = 200; // Adjust this value as needed
    
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, []);
  
  // Update arrow visibility on scroll
  useEffect(() => {
    const container = thumbnailContainerRef.current;
    if (container && needsThumbnailNav) {
      container.addEventListener('scroll', updateArrowVisibility);
      updateArrowVisibility(); // Initial check
      
      return () => {
        container.removeEventListener('scroll', updateArrowVisibility);
      };
    }
  }, [needsThumbnailNav, images.length]);
  
  // Update arrow visibility when images change
  useEffect(() => {
    updateArrowVisibility();
  }, [images]);

  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="220px"
        />
      </div>
    );
  };

  // Add navigation arrows to existing thumbnail container
  useEffect(() => {
    if (!needsThumbnailNav) return;

    // Function to setup arrows
    const setupArrows = () => {
      const thumbnailContainer = document.querySelector('.image-gallery-thumbnails');
      if (!thumbnailContainer) return false;

      // Set ref to the actual thumbnail container
      thumbnailContainerRef.current = thumbnailContainer;

      // Check if arrows already exist
      const existingLeftArrow = document.querySelector(`.${css.thumbnailNavLeft}`);
      const existingRightArrow = document.querySelector(`.${css.thumbnailNavRight}`);
      
      if (existingLeftArrow && existingRightArrow) {
        return true; // Arrows already exist
      }

      // Create navigation arrows
      const leftArrow = document.createElement('button');
      leftArrow.className = css.thumbnailNavLeft;
      leftArrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>';
      leftArrow.onclick = () => scrollThumbnails('left');
      leftArrow.setAttribute('aria-label', intl.formatMessage({ id: 'ListingImageGallery.scrollLeft' }));

      const rightArrow = document.createElement('button');
      rightArrow.className = css.thumbnailNavRight;
      rightArrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>';
      rightArrow.onclick = () => scrollThumbnails('right');
      rightArrow.setAttribute('aria-label', intl.formatMessage({ id: 'ListingImageGallery.scrollRight' }));

      // Add arrows to container
      thumbnailContainer.parentNode.appendChild(leftArrow);
      thumbnailContainer.parentNode.appendChild(rightArrow);

      // Initial arrow visibility check
      updateArrowVisibility();
      return true;
    };

    // Try to setup arrows immediately
    let setupSuccess = setupArrows();
    
    // If not successful, try again after a delay
    if (!setupSuccess) {
      const timer = setTimeout(() => {
        setupArrows();
      }, 100);

      // Cleanup function
      return () => {
        clearTimeout(timer);
        const leftArrow = document.querySelector(`.${css.thumbnailNavLeft}`);
        const rightArrow = document.querySelector(`.${css.thumbnailNavRight}`);
        
        if (leftArrow && leftArrow.parentNode) {
          leftArrow.parentNode.removeChild(leftArrow);
        }
        if (rightArrow && rightArrow.parentNode) {
          rightArrow.parentNode.removeChild(rightArrow);
        }
      };
    }

    // Cleanup function for immediate setup
    return () => {
      const leftArrow = document.querySelector(`.${css.thumbnailNavLeft}`);
      const rightArrow = document.querySelector(`.${css.thumbnailNavRight}`);
      
      if (leftArrow && leftArrow.parentNode) {
        leftArrow.parentNode.removeChild(leftArrow);
      }
      if (rightArrow && rightArrow.parentNode) {
        rightArrow.parentNode.removeChild(rightArrow);
      }
    };
  }, [needsThumbnailNav, images.length, intl, scrollThumbnails]);

  // Update arrow visibility when needed
  useEffect(() => {
    if (needsThumbnailNav) {
      const leftArrow = document.querySelector(`.${css.thumbnailNavLeft}`);
      const rightArrow = document.querySelector(`.${css.thumbnailNavRight}`);
      
      if (leftArrow) {
        leftArrow.style.display = showLeftArrow ? 'flex' : 'none';
      }
      if (rightArrow) {
        rightArrow.style.display = showRightArrow ? 'flex' : 'none';
      }
    }
  }, [showLeftArrow, showRightArrow, needsThumbnailNav]);

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FaExpand className={css.icon} />
        {/* <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        /> */}
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
